import { AdmissionRules } from '@quromedical/business-rules';
import { DiagnosisTypeCode, GroupSecurity } from '@quromedical/fhir-common';
import type {
  AdmissionCreateRequest,
  AdmissionGetResponseWithMedia,
  AdmissionLocationUpdateRequest,
  AllergyAdmissionCreateRequest,
  PatientCoverageResponse,
  PatientGeneralPractitionerUpdateRequest,
  PatientResponse,
} from '@quromedical/models';
import { compact, filter, first, isempty } from '@quromedical/utils';
import type { PatientRouteParams } from 'app/routes';
import { Row } from 'components/base';
import {
  type AdmissionClinicalData,
  AdmissionClinicalForm,
  mapDiagnosesToDiagnosisTypeCode,
} from 'core/admission';
// TODO: importing these from the correct location is creating an import error, probably a cycle
// eslint-disable-next-line no-restricted-imports
import {
  type AdmissionAuthorizationData,
  AdmissionAuthorizationForm,
} from 'core/admission/AdmissionAuthorizationForm';
import { AdmissionLocationForm } from 'core/admission/AdmissionLocationForm';
import {
  type AdmissionNotesData,
  AdmissionNotesForm,
} from 'core/admission/AdmissionNotesForm';
import { CareTeamForm } from 'core/patient';
import { PersonNameAndIdDisplay } from 'core/person';
import {
  Alert,
  DebounceButton,
  Section,
  Skeleton,
  SkeletonProvider,
  Snackbar,
} from 'design-system';
import { router, useLocalSearchParams } from 'expo-router';
import { logger } from 'helpers';
import { useFiniteState } from 'hooks/useFiniteState';
import { useObjectState } from 'hooks/useObjectState';
import { useRevalidation } from 'hooks/useRevalidation';
import { useUserSession } from 'hooks/useUserSession';
import {
  careTeamOrPractitionerFetcher,
  icd10CodeFetcher,
  locationWardFetcher,
  teamOrDepartmentOrganizationFetcher,
} from 'integration';
import {
  AdmissionApi,
  CoverageApi,
  PatientApi,
  PatientEndpoint,
} from 'integration/aggregate';
import type React from 'react';
import { useCallback, useState } from 'react';
import { ScrollView } from 'react-native-gesture-handler';
import { strings } from 'strings';
import useSWR from 'swr';

const admissionApi = new AdmissionApi();
const patientApi = new PatientApi();
const coverageApi = new CoverageApi();

interface FormData {
  note: AdmissionNotesData;
  authorization: AdmissionAuthorizationData;
  clinical: AdmissionClinicalData;
  careTeam: PatientGeneralPractitionerUpdateRequest;
  location: AdmissionLocationUpdateRequest;
  allergy: AllergyAdmissionCreateRequest;
}

type FormState =
  | 'interactive'
  | 'submitting'
  | 'validation-error'
  | 'submission-error'
  | 'loading'
  | 'complete';

interface ApiData {
  patient: PatientResponse;
  admissions: AdmissionGetResponseWithMedia[];
  coverage?: PatientCoverageResponse;
}

export const AdmissionCreateScreen: React.FC = () => {
  const { patientId } = useLocalSearchParams<PatientRouteParams>();
  const session = useUserSession();
  const [error, setError] = useState<string>();

  const swrKey = patientApi.getPatientSWRKey(
    patientId,
    PatientEndpoint.Base,
    PatientEndpoint.Coverage,
  );
  const apiData = useSWR(swrKey, async (): Promise<ApiData> => {
    // TODO: make a single endpoint for fetching just the necessary data for this page
    const patientAdmissions = await admissionApi.getAdmissions({
      patient: patientId,
      sort: '-date',
    });
    const admissions = patientAdmissions.admissions || [];
    const patient = await patientApi.getPatient(patientId);

    const coverage = await coverageApi.getCoverage(patientId);
    return {
      patient,
      admissions,
      coverage,
    };
  });

  const revalidate = useRevalidation(patientId);

  const formState = useFiniteState<FormState>('interactive');
  const formData = useObjectState<Partial<FormData>>({});

  const onSubmit = useCallback(async () => {
    formState.set('loading');

    const defined = apiData.data?.coverage
      ? formData.getDefined([
          'authorization',
          'clinical',
          'note',
          'careTeam',
          'location',
        ])
      : formData.getDefined([
          'clinical',
          'note',
          'careTeam',
          'location',
          'allergy',
        ]);

    if (!defined) {
      formState.set('validation-error');
      return;
    }

    const newAdmission: AdmissionCreateRequest = {
      class: defined.note.class,
      service: defined.note.service,
      periodStart: defined.note.periodStart,
      primaryIcd10Code: defined.authorization?.primaryIcd10Code as string,
      practitioners: defined.careTeam,
      reasonOfAdmission: [],
      location: defined.location?.location,
    };

    const admissions = apiData.data
      ?.admissions as AdmissionGetResponseWithMedia[];
    const canCreateAdmission =
      AdmissionRules.AdmissionRules.validateAdmissionRequest({
        activeAdmissions: admissions,
        admissionType: 'new-admission',
        newAdmission: newAdmission,
      });

    if (canCreateAdmission.type === 'error') {
      setError(canCreateAdmission.messages.join(', '));
      return formState.set('submission-error');
    }

    const primaryIcd10Code = mapDiagnosesToDiagnosisTypeCode({
      primaryIcd10Code: compact([defined?.authorization?.primaryIcd10Code]),
    });

    const icd10Code = mapDiagnosesToDiagnosisTypeCode({
      icd10Codes: defined?.authorization?.icd10Codes,
    });

    const diagnosis = compact([
      primaryIcd10Code,
      icd10Code,
      defined.clinical.diagnosis,
    ]).flat();

    const reasonOfAdmission = compact(
      diagnosis.map((d) => {
        const diagnosisCode = d.type === DiagnosisTypeCode.rfa;
        return diagnosisCode ? d.condition : undefined;
      }),
    );

    const allergy = filter(defined.clinical.allergy, (obj) => {
      const a = JSON.parse(JSON.stringify(obj));
      return !isempty(a);
    });

    try {
      await admissionApi.createAdmission({
        patientId: patientId,
        class: defined.note.class,
        service: defined.note.service,
        periodStart: defined.note.periodStart,
        primaryIcd10Code: defined.authorization?.primaryIcd10Code,
        icd10Codes: defined.authorization?.icd10Codes,
        practitioners: defined.careTeam,
        medicalAidAuthorization: defined.authorization?.medicalAidAuthorization,
        oxygen: defined.clinical.oxygen,
        diagnosis,
        pregnancy: defined.clinical.pregnancy,
        location: defined.location.location,
        allergy: allergy && allergy.length > 0 ? allergy : undefined,
        reasonOfAdmission,
      });

      await revalidate().catch(logger.error);
      router.push({ pathname: `/patient/${patientId}/clinical` });

      formState.set('complete');
    } catch (err) {
      logger.error(err);
      formState.set('submission-error');
    }
  }, [
    apiData.data?.coverage,
    apiData.data?.admissions,
    formData,
    formState,
    patientId,
    revalidate,
  ]);
  const inProgressAdmission = first(apiData.data?.admissions);

  const icd10Codes = inProgressAdmission?.icd10Codes?.map((code) => code.code);
  const acute = inProgressAdmission?.acute?.map((code) => code.code);
  const chronic = inProgressAdmission?.chronic?.map((code) => code.code);
  const reasonOfAdmission = inProgressAdmission?.reasonOfAdmission?.map(
    (code) => code.code,
  );
  const rfaIcd10Codes = { acute, chronic, reasonOfAdmission };

  if (apiData.error) {
    return (
      <Snackbar isOpen={!!apiData.error} onClose={revalidate}>
        <Alert
          backgroundColor="status-critical"
          textColor="white"
          onAction={revalidate}
          actionIcon="refresh"
          title={strings.AlertError}
          body={strings.ErrorFetchingPatient}
        />
      </Snackbar>
    );
  }

  const showErrors = formState.is('validation-error');

  const showLocation = session.hasAny([GroupSecurity.LocationRead]);

  return (
    <>
      <Snackbar
        isOpen={formState.is('submission-error')}
        onClose={formState.next('interactive')}
      >
        <Alert
          backgroundColor="status-critical"
          textColor="white"
          onAction={formState.next('interactive')}
          title={strings.ErrorCardTitle}
          body={error || strings.GenericErrorMessage}
          actionIcon="close"
        />
      </Snackbar>
      <SkeletonProvider
        loading={apiData.isValidating || formState.is('loading')}
      >
        <ScrollView>
          <Section hasTopMargin>
            <Skeleton>
              <PersonNameAndIdDisplay
                title={strings.CardLabelPatientDetails}
                general={apiData.data?.patient?.general}
              />
            </Skeleton>
          </Section>
          <Section unsetZIndex>
            <Skeleton>
              <AdmissionNotesForm
                onValidChange={formData.set('note')}
                showErrors={showErrors}
                patientId={patientId}
              />
            </Skeleton>
          </Section>
          <Section unsetZIndex isVisible={!!apiData.data?.coverage}>
            <Skeleton>
              <AdmissionAuthorizationForm
                schemeName={apiData.data?.coverage?.coverage?.scheme?.display}
                planName={apiData.data?.coverage?.coverage?.plan}
                onValidChange={formData.set('authorization')}
                showErrors={showErrors}
                icd10Codes={icd10Codes}
              />
            </Skeleton>
          </Section>
          <Section unsetZIndex>
            <Skeleton>
              <AdmissionClinicalForm
                patientId={patientId}
                icd10CodeFetcher={icd10CodeFetcher}
                // TODO: cleanup here. need to get ICD10Codes from previous admission
                rfaIcd10Codes={rfaIcd10Codes}
                onValidChange={formData.set('clinical')}
                showErrors={showErrors}
                isOnboarding
              />
            </Skeleton>
          </Section>
          <Section unsetZIndex>
            <Skeleton>
              <CareTeamForm
                // TODO: get practitioners from previous admission
                teamOrDepartmentOrganizationFetcher={
                  teamOrDepartmentOrganizationFetcher
                }
                onValidChange={formData.set('careTeam')}
                showErrors={showErrors}
                careTeamOrPractitionerFetcher={careTeamOrPractitionerFetcher}
                // TODO: set the default practitioners here, needs to be defined as a `CreateReference`
                // and create a mapping from a `CreateReference` to a `SelectOption` (search: mapRefTo)
              />
            </Skeleton>
          </Section>
          <Section unsetZIndex isVisible={showLocation}>
            <Skeleton>
              <AdmissionLocationForm
                locationFetcher={locationWardFetcher}
                onValidChange={formData.set('location')}
                showErrors={showErrors}
              />
            </Skeleton>
          </Section>
          <Section unsetZIndex hasBottomMargin>
            <Skeleton>
              <Row justifyContent="flex-end" unsetZIndex>
                <DebounceButton
                  disabled={apiData.isValidating || formState.is('loading')}
                  onPress={onSubmit}
                  text={strings.ButtonTextSubmit}
                />
              </Row>
            </Skeleton>
          </Section>
        </ScrollView>
      </SkeletonProvider>
    </>
  );
};
